<!--
  +---------------------------------------------------------------------------------------
  | @Copyright: Frippler Copyright (c)
  | @Title:     设备联网监控
  +---------------------------------------------------------------------------------------
-->

<template>
  <div class="em-monitor header-padding">
    <f-page-header />

    <div class="filter-container">
      <div class="filter-box">
        <span class="filter-item">
          <f-radio-btn-group
            v-model="query.status"
            :groups="radios"
            type="minor"
            @change="fetchData(1)"
          />
        </span>

        <span class="filter-item sm">
          <f-search-input v-model="query.keyword" @change="fetchData(1)" />
        </span>
      </div>

      <div class="toolbox">
        <el-button type="success" icon="el-icon-plus" @click="edit()">新建设备</el-button>
        <el-button type="success" icon="el-icon-upload2" plain @click="importShow = true">导入</el-button>
      </div>
    </div>

    <el-table
      v-loading="loading"
      ref="listTable"
      :data="list"
      style="width: 100%"
      :show-header="false"
      size="medium"
      class="padding-small"
      @selection-change="handleSelectionChange"
    >
      <el-table-column width="30"></el-table-column>

      <el-table-column type="selection" width="60"></el-table-column>

      <el-table-column label="设备" min-width="140">
        <template slot-scope="scope">
          <div
            role="button"
            class="preview"
            :style="{ 'background-image': scope.row.equip_avatar ? `url(${scope.row.equip_avatar})` : `url(${nopic})` }"
            @click="toDetail(scope.row)"
          >
            <div class="comm-tags">
              <el-tag
                v-if="scope.row.is_maintain"
                type="danger"
                effect="dark"
                class="comm-tag"
              >设备维护中</el-tag>
              <el-tag
                v-else-if="scope.row.is_online"
                type="success"
                effect="dark"
                class="comm-tag"
              >通讯正常</el-tag>
              <el-tag v-else type="info" effect="dark" class="comm-tag">通讯断开</el-tag>
            </div>

            <div class="updatetime flex-center">
              <i v-if="scope.row.is_online" class="el-icon-refresh" />
              <i v-else class="iconfont icon-disconnect" />
              <span
                v-if="scope.row.is_alarm"
                class="time ml-xs"
              >{{ scope.row.last_alarmon_time | parseTime("{y}-{m}-{d} {h}:{i}:{s}") }}</span>
              <span
                v-else-if="scope.row.is_running"
                class="time ml-xs"
              >{{ scope.row.last_parse_time | parseTime("{y}-{m}-{d} {h}:{i}:{s}") }}</span>
              <span
                v-else-if="scope.row.is_online"
                class="time ml-xs"
              >{{ scope.row.last_online_time | parseTime("{y}-{m}-{d} {h}:{i}:{s}") }}</span>
              <span
                v-else
                class="time ml-xs"
              >{{ scope.row.last_offline_time | parseTime("{y}-{m}-{d} {h}:{i}:{s}") }}</span>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="设备名称" min-width="160">
        <template slot-scope="scope">
          <el-link
            :underline="false"
            class="font-18 mb-xs"
            @click="toDetail(scope.row)"
          >{{ scope.row.equip_name }}</el-link>
          <div
            class="text-muted mb-xs"
            role="button"
            @click="toDetail(scope.row)"
          >{{ scope.row.go }}</div>
          <el-tag v-if="!scope.row.is_online" type="info" class="tag">停机</el-tag>
          <el-tag
            v-if="scope.row.is_online && scope.row.is_running && !scope.row.is_alarm"
            type="success"
            class="tag"
          >运行</el-tag>
          <el-tag v-if="scope.row.is_online && scope.row.is_alarm" type="danger" class="tag">告警</el-tag>
          <div v-if="scope.row.is_online && scope.row.is_alarm" class="text-danger mt-xs">
            {{ scope.row.last_alarm_title
            }}
          </div>
          <el-tag
            v-if="scope.row.is_online && !scope.row.is_running && !scope.row.is_alarm"
            type="warning"
            class="tag"
          >待机</el-tag>
        </template>
      </el-table-column>

      <el-table-column label="设备数据" min-width="500px">
        <template slot-scope="scope">
          <div class="flex">
            <div v-for="(item, index) in scope.row.sensors" :key="index">
              <div class="sen-box" :class="{ 'border': index}">
                <div class="sen-title font-13 text-muted">{{ item.sen_name }}</div>
                <div class="sen-value flex justify-content-center align-items-end mt-md">
                  <!-- 数值型 -->
                  <template v-if="item.sen_type === 1">
                    <div class="number font-18 text-bold">{{ item.value ? item.value : '--' }}</div>
                    <div v-if="item.unit" class="text-muted unit ml-xs">{{ item.unit }}</div>
                  </template>

                  <!-- 档位型 -->
                  <template v-if="item.sen_type === 2">
                    <div class="number font-18 text-bold">{{ item.value ? item.value : '--' }}</div>
                  </template>

                  <!-- 开关型 -->
                  <span v-if="item.sen_type === 3">
                    <template v-if="item.icon_url && item.icon_url_2">
                      <img v-if="item.value === '1'" class="image mini" :src="item.icon_url_2" />
                      <img v-else class="image mini" :src="item.icon_url" />
                    </template>
                    <template v-else>
                      <img
                        v-if="item.value === '1'"
                        class="image mini"
                        src="@/assets/default-sensor-on.svg"
                      />
                      <img v-else class="image mini" src="@/assets/default-sensor-off.svg" />
                    </template>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column label="操作" align="right" width="40">
        <template slot-scope="scope">
          <div class style="height: 130px;">
            <el-popover
              placement="left-start"
              width="120"
              trigger="hover"
              popper-class="tool-pop"
              :visible-arrow="false"
            >
              <div class="pop-item" role="button" @click="edit(scope.row)">编辑</div>
              <div
                v-if="!scope.row.is_maintain"
                class="pop-item"
                role="button"
                @click="maintain(scope.row)"
              >开始维护</div>
              <div v-else class="pop-item" role="button" @click="maintain(scope.row)">维护完成</div>
              <div class="pop-item" role="button" @click="deleteOne(scope.row)">删除</div>
              <div class="font-lg" slot="reference">
                <i class="iconfont icon-more more" role="button"></i>
              </div>
            </el-popover>
          </div>
        </template>
      </el-table-column>

      <el-table-column width="30"></el-table-column>

      <template slot="empty">
        <img class="image huger mt-lg" src="@/assets/empty.png" />
        <div class="font-md mb-hg">暂无数据</div>
      </template>
    </el-table>

    <f-table-footer
      checkbox
      :list="list"
      :selection="multiSelection"
      @toggle="toggleAll"
      :total="total"
      :pindex.sync="query.pindex"
      :psize.sync="query.psize"
      @change="fetchData"
    >
      <template slot="left">
        <el-button plain :disabled="multiSelection.length === 0" size="mini" @click="deleteMulti">删除</el-button>
        <el-button class="ml-lg" plain :disabled="total === 0" size="mini" @click="exportE">导出</el-button>
      </template>
    </f-table-footer>

    <em-equip-import v-if="importShow" :show.sync="importShow" @submit="fetchData(1)" />

    <new-equip
      v-if="dialogShow"
      :show.sync="dialogShow"
      :equipid="activeEquipid"
      @success="fetchData(1)"
    />
  </div>
</template>

<script>
import {
  getEquipListApi,
  exportEquipApi,
  delEquipApi,
  equipMaintainApi,
} from "@/api/em/monitorApi";

export default {
  name: "MesCreate",

  components: {
    EmEquipImport: () => import("@/views/em/components/EmEquipImport"),
    NewEquip: () => import("@/views/em/components/NewEquip"),
  },

  data() {
    return {
      nopic: require("@/assets/no-pic.png"),
      dialogShow: false,
      importShow: false,
      activeEquipid: null,
      query: {
        keyword: "",
        status: 0,
        pindex: 1,
        psize: this.$store.state.commonStore.psize,
      },
      total: 0,
      multiSelection: [],
      radios: [
        { value: 0, title: "全部设备", separate: true },
        { value: 1, title: "停机" },
        { value: 2, title: "待机" },
        { value: 3, title: "运行" },
        { value: 4, title: "故障" },
      ],
      steps: this.$store.state.mesStore.dispatchProcessArr,
      list: [],
      senlist: [1, 1, 1, 1, 1, 1],
      loading: false,
    };
  },

  computed: {},

  created() {
    this.fetchData();
  },

  methods: {
    fetchData(page, loading = true) {
      if (page) this.query.pindex = page;
      if (loading) this.loading = true;
      getEquipListApi(this.query).then((res) => {
        this.loading = false;
        if (!res.errno) {
          this.list = res.data.list;
          this.total = res.data.total;
        }
      });
    },

    handleSelectionChange(val) {
      this.multiSelection = val;
    },

    toggleAll() {
      this.$refs.listTable.toggleAllSelection();
    },

    edit(row) {
      this.activeEquipid = row ? row.equipid : null;
      this.dialogShow = true;
    },

    maintain(item) {
      if (this.dialogShow) return;
      this.$confirm(
        !item.is_maintain
          ? `即将维护设备<span class="text-primary"> ${item.equip_name}</span> ，是否继续 ？`
          : `即将完成设备<span class="text-primary"> ${item.equip_name}</span> 的维护，是否继续 ？`,
        "提示",
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      ).then(() => {
        this.loading = true;
        const temp = {
          equipid: item.equipid,
          is_maintain: item.is_maintain ? 0 : 1,
        };
        equipMaintainApi(temp).then((res) => {
          this.loading = false;
          if (!res.errno) {
            this.$message.success("操作成功！");
            this.fetchData();
          } else {
            this.$message.error(res.message);
          }
        });
      });
    },

    deleteOne(item) {
      if (this.dialogShow) return;
      this.$confirm(
        `即将删除设备<span class="text-primary"> ${item.equip_name}</span>，是否继续 ？`,
        "提示",
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => {
          this.loading = true;
          delEquipApi({ equipids: [item.equipid] }).then((res) => {
            this.loading = false;
            if (!res.errno) {
              this.$message.success("设备删除成功！");
              this.fetchData();
            } else {
              this.$message.error(res.message);
            }
          });
        })
        .catch(() => {});
    },

    deleteMulti() {
      let tmp = this.multiSelection.map((v) => v.equipid);
      this.$confirm(
        `即将删除<span class="text-primary"> ${tmp.length}</span> 台设备，是否继续 ？`,
        "提示",
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => {
          this.loading = true;
          delEquipApi({ equipids: tmp }).then((res) => {
            this.loading = false;
            if (!res.errno) {
              this.$message.success("设备删除成功！");
              this.fetchData();
            } else {
              this.$message.error(res.message);
            }
          });
        })
        .catch(() => {});
    },

    exportE() {
      exportEquipApi(this.query).then((res) => {
        if (!res.errno) {
          window.open(res.data.url);
          this.$message.success("设备导出成功！");
        }
      });
    },

    toDetail(row) {
      const { stationid } = row;
      const _this = this;
      this.$sdetail({
        stationid,
        handleChange: () => {
          _this.fetchData();
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
/deep/ .el-table {
  border-top: 0;
  border-left: 0;
  border-right: 0;
  background: transparent;
}

/deep/ .el-table th.is-leaf,
/deep/ .el-table td,
/deep/ .el-table__empty-block {
  border-bottom: 1px solid transparent;
  background: rgb(248, 248, 249);
}

/deep/ .el-table tr {
  margin: 20px 0;
  background: transparent;
  padding: 20px;
}

// /deep/ .el-table--enable-row-hover .el-table__body tr:hover > td {
// background-color: #fff;
// }
/deep/ .el-table__body {
  border-collapse: separate !important;
  border-spacing: 0 15px !important;
  table-layout: auto !important;
  margin-top: -15px;
}

/deep/ .el-table-column--selection .cell {
  height: 130px;
}

.em-monitor {
  position: relative;

  .preview {
    position: relative;
    width: 175px;
    height: 130px;
    margin: 8px 0;
    border-radius: 4px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    .comm-tags {
      .comm-tag {
        height: 23px;
        line-height: 23px;
        border-radius: 4px 0px 4px 0px;
      }
    }

    .updatetime {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 175px;
      height: 24px;
      border-radius: 0px 0px 4px 4px;
      background-color: rgba(16, 16, 16, 0.5);
      color: #fff;
    }
  }

  .tag {
    min-width: 72px;
    text-align: center;
  }

  .sen-box {
    width: 100px;
    height: 100px;
    background: #eef0f5;
    padding: 8px;

    .sen-value {
      line-height: 28px;
    }

    .unit {
      line-height: 22px;
    }
  }

  .border {
    border-left: 1px solid #eaeaea;
  }

  .more {
    font-size: 40px;
    line-height: 40px;
    color: #999;
  }
}
</style>
